import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { CalendarClock, ImagePlus, ListChecks, MapPin, Save, Trash2, X } from 'lucide-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

import EventDateTimePicker from './EventDateTimePicker';
import GooglePlacePicker from './GooglePlacePicker';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const defaultTimezone = () => (
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
);

const supportedTimezones = () => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [];
  }
};

const emptyForm = () => ({
  title: '',
  description: '',
  startsAt: '',
  endsAt: '',
  timezone: defaultTimezone(),
  locationName: '',
  locationAddress: '',
  locationPlaceId: '',
  locationLatitude: '',
  locationLongitude: '',
});

const localInputValue = (instant, timezone) => {
  if (!instant) return '';
  return dayjs(instant).tz(timezone || defaultTimezone()).format('YYYY-MM-DDTHH:mm');
};

const formFromEvent = (event) => {
  if (!event) return emptyForm();
  const eventTimezone = event.timezone || defaultTimezone();
  return {
    title: event.title || '',
    description: event.description || '',
    startsAt: localInputValue(event.starts_at, eventTimezone),
    endsAt: localInputValue(event.ends_at, eventTimezone),
    timezone: eventTimezone,
    locationName: event.location?.name || '',
    locationAddress: event.location?.address || '',
    locationPlaceId: event.location?.place_id || '',
    locationLatitude: event.location?.latitude ?? '',
    locationLongitude: event.location?.longitude ?? '',
  };
};

const arcadeButton = (colorKey = 'primary') => (theme) => {
  const color = theme.palette[colorKey]?.main || theme.palette.primary.main;
  return {
    m: 0,
    minHeight: 48,
    color: theme.palette.background.default,
    bgcolor: color,
    borderColor: color,
    boxShadow: `0 6px 0 ${darken(color, 0.58)}, 0 0 18px ${alpha(color, 0.32)}`,
    transition: 'transform 140ms ease, box-shadow 140ms ease',
    '&:hover': {
      color: theme.palette.background.default,
      bgcolor: color,
      transform: 'translate(-2px, -2px)',
      boxShadow: `2px 8px 0 ${darken(color, 0.58)}, 0 0 24px ${alpha(color, 0.5)}`,
    },
    '&:active': {
      transform: 'translateY(4px)',
      boxShadow: `0 2px 0 ${darken(color, 0.58)}`,
    },
  };
};

export default function EventForm({
  event = null,
  saving = false,
  onSubmit,
  onCancel,
  onManageTasks,
}) {
  const [form, setForm] = useState(() => formFromEvent(event));
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const imageInputRef = useRef(null);
  const browserTimezone = defaultTimezone();
  const timezoneOptions = useMemo(() => {
    const otherTimezones = supportedTimezones()
      .filter((timezone) => timezone !== browserTimezone && timezone !== form.timezone)
      .sort((left, right) => left.localeCompare(right));
    return Array.from(new Set([browserTimezone, form.timezone, 'UTC', ...otherTimezones]));
  }, [browserTimezone, form.timezone]);

  useEffect(() => {
    if (!imageFile || typeof URL.createObjectURL !== 'function') {
      setImagePreview('');
      return undefined;
    }
    const preview = URL.createObjectURL(imageFile);
    setImagePreview(preview);
    return () => URL.revokeObjectURL(preview);
  }, [imageFile]);

  const setField = (field) => (inputEvent) => {
    setForm((current) => ({ ...current, [field]: inputEvent.target.value }));
  };

  const setValue = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const setLocation = (location) => {
    setForm((current) => ({
      ...current,
      locationName: location?.name || '',
      locationAddress: location?.address || '',
      locationPlaceId: location?.place_id || '',
      locationLatitude: location?.latitude ?? '',
      locationLongitude: location?.longitude ?? '',
    }));
  };

  const submit = async (submitEvent) => {
    submitEvent.preventDefault();
    setError('');
    try {
      if (!form.title.trim()) throw new Error('Add an event title.');
      if (!form.startsAt || !form.endsAt) throw new Error('Choose a start and end time.');
      const startsAt = dayjs.tz(form.startsAt, form.timezone);
      const endsAt = dayjs.tz(form.endsAt, form.timezone);
      if (!startsAt.isValid() || !endsAt.isValid()) throw new Error('Choose valid event times.');
      if (!endsAt.isAfter(startsAt)) throw new Error('The end time must be after the start time.');
      await onSubmit({
        title: form.title.trim(),
        description: form.description.trim() || null,
        starts_at: startsAt.toISOString(),
        ends_at: endsAt.toISOString(),
        timezone: form.timezone.trim(),
        location: {
          name: form.locationName.trim() || null,
          address: form.locationAddress.trim() || null,
          place_id: form.locationPlaceId.trim() || null,
          latitude: form.locationLatitude === '' ? null : Number(form.locationLatitude),
          longitude: form.locationLongitude === '' ? null : Number(form.locationLongitude),
        },
      }, { file: imageFile, remove: removeImage });
    } catch (submitError) {
      setError(
        submitError.data?.message || submitError.message || 'Unable to save the event.'
      );
    }
  };

  const fieldSx = {
    '& .MuiInputBase-root': { borderRadius: 3 },
    '& .MuiInputBase-input': { fontSize: { xs: '0.72rem', sm: '0.82rem' } },
  };

  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={(theme) => ({
        p: { xs: 2, sm: 3 },
        border: `3px solid ${theme.palette.primary.main}`,
        borderRadius: 5,
        background:
          `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.98)}, ` +
          `${alpha(theme.palette.primary.main, 0.08)})`,
        boxShadow:
          `0 10px 0 ${alpha(theme.palette.primary.main, 0.2)}, ` +
          `0 0 35px ${alpha(theme.palette.primary.main, 0.18)}`,
      })}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main' }}>
            {event ? 'Edit event' : 'Create event'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'secondary.main', textAlign: 'left' }}>
            Keep the details clear so the event is easy to scan later.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ color: 'secondary.main' }}>{error}</Alert>}

        <TextField
          required
          label="Event title"
          value={form.title}
          onChange={setField('title')}
          inputProps={{ maxLength: 200 }}
          sx={fieldSx}
        />

        <Stack spacing={1.5} alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'secondary.main' }}>
            <ImagePlus size={20} />
            <Typography variant="h6" sx={{ color: 'secondary.main' }}>Event image</Typography>
          </Stack>
          <Box
            sx={(theme) => ({
              width: '100%',
              minHeight: 150,
              maxHeight: 320,
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              border: `2px solid ${theme.palette.secondary.main}`,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.secondary.main, 0.07),
            })}
          >
            {imagePreview || (!removeImage && event?.image?.url) ? (
              <Box
                component="img"
                src={imagePreview || event.image.url}
                alt="Event preview"
                sx={{ width: '100%', height: '100%', maxHeight: 320, objectFit: 'cover' }}
              />
            ) : (
              <Stack spacing={1} alignItems="center" sx={{ py: 4, color: 'secondary.main' }}>
                <ImagePlus size={36} />
                <Typography variant="caption" sx={{ color: 'secondary.main' }}>
                  Add an image for this event
                </Typography>
              </Stack>
            )}
          </Box>
          <input
            ref={imageInputRef}
            hidden
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(inputEvent) => {
              const file = inputEvent.target.files?.[0];
              if (file) {
                setImageFile(file);
                setRemoveImage(false);
              }
              inputEvent.target.value = '';
            }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              type="button"
              variant="outlined"
              startIcon={<ImagePlus size={17} />}
              disabled={saving}
              onClick={() => imageInputRef.current?.click()}
              sx={arcadeButton('primary')}
            >
              {imagePreview || (!removeImage && event?.image) ? 'Change image' : 'Add image'}
            </Button>
            {(imageFile || (!removeImage && event?.image)) && (
              <Button
                type="button"
                variant="outlined"
                startIcon={<Trash2 size={17} />}
                disabled={saving}
                onClick={() => {
                  setImageFile(null);
                  setRemoveImage(Boolean(event?.image));
                }}
                sx={arcadeButton('secondary')}
              >
                Remove image
              </Button>
            )}
          </Stack>
        </Stack>
        <TextField
          label="Description"
          value={form.description}
          onChange={setField('description')}
          multiline
          minRows={3}
          inputProps={{ maxLength: 10000 }}
          sx={fieldSx}
        />

        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'primary.main' }}>
          <CalendarClock size={20} />
          <Typography variant="h6" sx={{ color: 'primary.main' }}>Date and time</Typography>
        </Stack>
        <EventDateTimePicker
          startsAt={form.startsAt}
          endsAt={form.endsAt}
          onChange={setValue}
        />
        <TextField
          required
          select
          label="Timezone"
          value={form.timezone}
          onChange={setField('timezone')}
          SelectProps={{ native: true }}
          sx={{
            ...fieldSx,
            '& .MuiFormHelperText-root': { color: 'secondary.main' },
            '& option': {
              color: 'primary.main',
              backgroundColor: 'background.default',
            },
          }}
        >
          {timezoneOptions.map((timezone) => (
            <option key={timezone} value={timezone}>
              {timezone === browserTimezone ? `${timezone} (Browser timezone)` : timezone}
            </option>
          ))}
        </TextField>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'primary.main' }}>
          <MapPin size={20} />
          <Typography variant="h6" sx={{ color: 'primary.main' }}>Location</Typography>
        </Stack>
        <GooglePlacePicker
          label="Search for the event destination"
          value={{
            name: form.locationName,
            address: form.locationAddress,
            place_id: form.locationPlaceId,
            latitude: form.locationLatitude,
            longitude: form.locationLongitude,
          }}
          onChange={setLocation}
          disabled={saving}
          editableName
        />

        <Stack spacing={1} alignItems="flex-start">
          <Button
            type="button"
            variant="outlined"
            startIcon={<ListChecks size={18} />}
            disabled={saving || !event}
            onClick={onManageTasks}
            sx={arcadeButton('orange')}
          >
            Add tasks to event
          </Button>
          {!event && (
            <Typography variant="caption" sx={{ color: 'secondary.main', textAlign: 'left' }}>
              Save the event first, then you can add tasks.
            </Typography>
          )}
        </Stack>

        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" spacing={2}>
          <Button
            type="button"
            variant="outlined"
            startIcon={<X size={18} />}
            onClick={onCancel}
            disabled={saving}
            sx={arcadeButton('secondary')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Save size={18} />}
            disabled={saving}
            sx={arcadeButton('primary')}
          >
            {saving ? 'Saving...' : (event ? 'Save event' : 'Create event')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
